import { Injectable } from '@angular/core';
import { Observable, interval } from 'rxjs';
import { map, takeWhile, share } from 'rxjs/operators';

import {
  InverterV2,
  VirtualRTAzzurroHubTarget,
  VirtualRTStateType,
  VirtualRTTelemetryType,
} from '../telemetry';

/**
 * Flag to enable/disable mock mode.
 * Keep false by default now that cloud RT is available.
 */
export const VIRTUAL_RT_MOCK_ENABLED = false;

@Injectable({
  providedIn: 'root',
})
export class VirtualRTMockService {
  private activeSessions = new Map<string, { startTS: number; duration: number; target: string }>();

  private dailyAccumulators = new Map<string, {
    EG1: number; EG2: number; EG3: number;
    EG4: number; EG5: number; EG6: number;
  }>();

  async activateMock(azzurroHubId: string, duration: number, target: string): Promise<VirtualRTStateType> {
    const startTS = Date.now();
    this.activeSessions.set(azzurroHubId, { startTS, duration, target });
    this.dailyAccumulators.set(azzurroHubId, {
      EG1: 0, EG2: 0, EG3: 0, EG4: 0, EG5: 0, EG6: 0,
    });
    return { active: true, startTS, duration, target };
  }

  subscribeMock(azzurroHubId: string): Observable<VirtualRTTelemetryType> {
    const session = this.activeSessions.get(azzurroHubId);
    const startTS = session?.startTS ?? Date.now();
    const duration = session?.duration ?? 300;

    if (!this.dailyAccumulators.has(azzurroHubId)) {
      this.dailyAccumulators.set(azzurroHubId, {
        EG1: 0, EG2: 0, EG3: 0, EG4: 0, EG5: 0, EG6: 0,
      });
    }

    return interval(5000).pipe(
      takeWhile(() => Date.now() < startTS + duration * 1000),
      map(() => this.generatePayload(azzurroHubId)),
      share(),
    );
  }

  async deactivateMock(azzurroHubId: string, target: string): Promise<VirtualRTStateType> {
    this.activeSessions.delete(azzurroHubId);
    this.dailyAccumulators.delete(azzurroHubId);
    return { active: false, startTS: 0, duration: 0, target };
  }

  private generatePayload(azzurroHubId: string): VirtualRTTelemetryType {
    const gatewaySN = azzurroHubId.replace(/^ZZ/, '').replace(/Z$/, '');
    return {
      deviceId: azzurroHubId,
      loggerId: azzurroHubId,
      target: VirtualRTAzzurroHubTarget.ZZ,
      ZZ: this.generateRealTimeData(azzurroHubId, gatewaySN),
    };
  }

  private generateRealTimeData(deviceId: string, gatewaySN: string): InverterV2 {
    const now = new Date();
    const hour = now.getHours() + now.getMinutes() / 60;

    // Solar curve: bell shape peaking at 12:30, values in WATTS (real scale ~8000-9000W peak)
    const solarPeakW = 9000;
    const solarHour = hour - 12.5;
    const P_DC_TOT = hour >= 6 && hour <= 19
      ? solarPeakW * Math.exp(-0.5 * Math.pow(solarHour / 3, 2)) * (1 + (Math.random() - 0.5) * 0.1)
      : 0;

    // Load: realistic range ~13000-16000W
    const P_LOAD = 13000 + Math.random() * 3000;

    // Battery: 0 or small values (real data shows "0" most of the time)
    const P_B_AGGR = Math.random() > 0.8 ? round((Math.random() - 0.5) * 500, 0) : 0;

    // Grid: P_EXP is negative (export convention), P_EXT is positive (import)
    const surplus = P_DC_TOT - P_LOAD - P_B_AGGR;
    const P_EXP = surplus > 0 ? -surplus : surplus; // always negative or near zero
    const P_EXT = Math.max(0, P_LOAD + P_B_AGGR - P_DC_TOT);

    // Battery percentage: low, slowly varying around 15%
    const BP = Math.max(0, Math.min(100, 15 + 5 * Math.sin(Date.now() / 600000)));

    // SOL_IRR and SOL_TEMP: null (matches real AzzurroHub data)
    const SOL_IRR = 0;
    const SOL_TEMP = 0;
    const SOL_EG = 0;

    // Daily energy accumulators (Wh, increment every 5s)
    const acc = this.dailyAccumulators.get(deviceId)!;
    const intervalHours = 5 / 3600; // 5 seconds in hours
    acc.EG1 += P_DC_TOT * intervalHours;
    acc.EG2 += Math.abs(P_EXP) * intervalHours;
    acc.EG3 += P_EXT * intervalHours;
    acc.EG4 += P_LOAD * intervalHours;
    acc.EG5 += Math.max(0, P_B_AGGR) * intervalHours;
    acc.EG6 += Math.max(0, -P_B_AGGR) * intervalHours;

    // Total energies: base (large totals) + daily accumulation
    const baseTotalWh = 15000000; // ~15 MWh in Wh
    const ET1 = baseTotalWh + acc.EG1;
    const ET2 = baseTotalWh * 0.3 + acc.EG2;
    const ET3 = baseTotalWh * 0.2 + acc.EG3;
    const ET4 = baseTotalWh * 0.7 + acc.EG4;
    const ET5 = baseTotalWh * 0.15 + acc.EG5;
    const ET6 = baseTotalWh * 0.15 + acc.EG6;

    // Alerts: mostly empty (NULL in real data)
    const ALERTS = Math.random() > 0.98 ? 'W001' : '';

    return {
      ALERTS,
      BP: `${round(BP, 0)}`,
      EG1: `${round(acc.EG1, 2)}`,
      EG2: `${round(acc.EG2, 2)}`,
      EG3: `${round(acc.EG3, 2)}`,
      EG4: `${round(acc.EG4, 2)}`,
      EG5: `${round(acc.EG5, 2)}`,
      EG6: `${round(acc.EG6, 2)}`,
      ET1: `${round(ET1, 2)}`,
      ET2: `${round(ET2, 2)}`,
      ET3: `${round(ET3, 2)}`,
      ET4: `${round(ET4, 2)}`,
      ET5: `${round(ET5, 2)}`,
      ET6: `${round(ET6, 2)}`,
      GwId: gatewaySN,
      IANA: Intl.DateTimeFormat().resolvedOptions().timeZone,
      P_B_AGGR: `${round(P_B_AGGR, 0)}`,
      P_DC_TOT: `${round(P_DC_TOT, 0)}`,
      P_EXP: `${round(P_EXP, 0)}`,
      P_EXT: `${round(P_EXT, 0)}`,
      P_LOAD: `${round(P_LOAD, 0)}`,
      PK: deviceId,
      SOL_EG,
      SOL_IRR,
      SOL_TEMP,
      TS: Math.floor(Date.now() / 1000),
    } as unknown as InverterV2;
  }
}

function round(value: number, decimals: number): number {
  const factor = Math.pow(10, decimals);
  return Math.round(value * factor) / factor;
}
